import React, { useEffect, useRef, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGoogleCallbackMutation } from '../api/auth';
import Logo from '../components/Logo';
import { useAuth } from '../providers/AppAuthProvider';

const GoogleOAuthCallback: React.FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { handleGoogleAuth } = useAuth();
	const [googleCallback] = useGoogleCallbackMutation();

	const hasProcessed = useRef(false);
	const [status, setStatus] = useState<'processing' | 'success' | 'error'>(
		'processing',
	);
	const [message, setMessage] = useState('Processing Google authentication...');

	useEffect(() => {
		// Prevent multiple executions
		if (hasProcessed.current) {
			return;
		}

		const handleCallback = async () => {
			try {
				const code = searchParams.get('code');
				const error = searchParams.get('error');
				const state = searchParams.get('state');

				if (error) {
					setStatus('error');
					setMessage(`Authentication failed: ${error}`);
					hasProcessed.current = true;
					return;
				}

				if (!code) {
					setStatus('error');
					setMessage('No authorization code received from Google');
					hasProcessed.current = true;
					return;
				}

				hasProcessed.current = true;

				try {
					// Pass state parameter to callback API
					const authResult = await googleCallback({
						code,
						state: state || undefined,
					}).unwrap();

					if (authResult.success) {
						// Update authentication state
						handleGoogleAuth(authResult);

						setStatus('success');
						setMessage(
							'Authentication successful! Redirecting to dashboard...',
						);

						// Redirect to dashboard
						setTimeout(() => {
							navigate('/', { replace: true });
						}, 500);
					} else {
						setStatus('error');
						setMessage(authResult.message || 'Google authentication failed');
					}
				} catch (err: any) {
					setStatus('error');
					setMessage(
						err?.data?.message ||
							err.message ||
							'Failed to complete authentication',
					);
					console.error('Google callback error:', err);
				}
			} catch (err) {
				setStatus('error');
				setMessage('An error occurred during authentication');
				console.error('OAuth callback error:', err);
			}
		};

		handleCallback();
	}, []);

	return (
		<div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
			<div className="sm:mx-auto sm:w-full sm:max-w-md">
				<div className="flex justify-center">
					<Logo />
				</div>
				<h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
					Google Authentication
				</h2>
			</div>

			<div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
				<div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
					<div className="text-center">
						{status === 'processing' && (
							<div className="flex flex-col items-center">
								<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
								<p className="text-gray-600">{message}</p>
							</div>
						)}

						{status === 'success' && (
							<div className="flex flex-col items-center">
								<div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
									<svg
										className="w-6 h-6 text-green-600"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
								</div>
								<p className="text-green-600 font-medium">{message}</p>
							</div>
						)}

						{status === 'error' && (
							<div className="flex flex-col items-center">
								<div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
									<svg
										className="w-6 h-6 text-red-600"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</div>
								<p className="text-red-600 font-medium">{message}</p>
								<button
									onClick={() => navigate('/login')}
									className="mt-4 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
								>
									Back to Login
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default GoogleOAuthCallback;
